import React from 'react';
import type { Product, ProductAttribute, WooCategory } from '../integrations/woocommerce/types';

export interface CompareItem {
  id: number;
  name: string;
  slug?: string;
  image?: string;
  price?: string;
  regularPrice?: string;
  salePrice?: string;
  stockStatus?: Product['stock_status'];
  attributes?: ProductAttribute[];
  categories?: WooCategory[];
}

interface CompareContextValue {
  items: CompareItem[];
  itemCount: number;
  maxItems: number;
  addToCompare: (item: CompareItem) => boolean;
  removeFromCompare: (id: number) => void;
  toggleCompare: (item: CompareItem) => void;
  clearCompare: () => void;
  isCompared: (id: number) => boolean;
  limitMessage: string;
  clearLimitMessage: () => void;
}

const STORAGE_KEY = 'nex-product-comparison';
const MAX_ITEMS = 4;
const CompareContext = React.createContext<CompareContextValue | undefined>(undefined);

const readStoredItems = (): CompareItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && Number.isFinite(Number(item.id)) && item.name)
      .slice(0, MAX_ITEMS)
      .map((item) => ({ ...item, id: Number(item.id), name: String(item.name) }));
  } catch {
    return [];
  }
};

export const CompareProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [items, setItems] = React.useState<CompareItem[]>(readStoredItems);
  const [limitMessage, setLimitMessage] = React.useState('');

  React.useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCompare = React.useCallback((item: CompareItem): boolean => {
    let added = false;
    setItems((current) => {
      if (current.some((entry) => entry.id === item.id)) return current;
      if (current.length >= MAX_ITEMS) {
        setLimitMessage(`You can compare up to ${MAX_ITEMS} products at a time.`);
        return current;
      }
      added = true;
      setLimitMessage('');
      return [...current, item];
    });
    return added;
  }, []);

  const removeFromCompare = React.useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
    setLimitMessage('');
  }, []);

  const toggleCompare = React.useCallback((item: CompareItem) => {
    setItems((current) => {
      if (current.some((entry) => entry.id === item.id)) {
        setLimitMessage('');
        return current.filter((entry) => entry.id !== item.id);
      }
      if (current.length >= MAX_ITEMS) {
        setLimitMessage(`You can compare up to ${MAX_ITEMS} products at a time.`);
        return current;
      }
      setLimitMessage('');
      return [...current, item];
    });
  }, []);

  const clearCompare = React.useCallback(() => {
    setItems([]);
    setLimitMessage('');
  }, []);

  const isCompared = React.useCallback((id: number) => items.some((item) => item.id === id), [items]);

  const value = React.useMemo(
    () => ({
      items,
      itemCount: items.length,
      maxItems: MAX_ITEMS,
      addToCompare,
      removeFromCompare,
      toggleCompare,
      clearCompare,
      isCompared,
      limitMessage,
      clearLimitMessage: () => setLimitMessage(''),
    }),
    [addToCompare, clearCompare, isCompared, items, limitMessage, removeFromCompare, toggleCompare],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = (): CompareContextValue => {
  const context = React.useContext(CompareContext);
  if (!context) throw new Error('useCompare must be used within CompareProvider');
  return context;
};
